import cuid from "cuid";
import { get } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAssignmentTurnedIn,
  MdOutlineDescription,
  MdOutlineInventory2,
  MdRoute,
} from "react-icons/md";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_DISTRIBUTION,
  DELETE_DISTRIBUTION,
} from "utils/api/graphql/mutations/distributions";
import { DISTRIBUTIONS } from "utils/api/graphql/queries/distributions";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  CONTENT_TYPES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
} from "utils/constants";
import CommunicationSection from "./config-sections/CommunicationSection";
import ContractSection from "./config-sections/ContractSection";
import DocumentsSection from "./config-sections/DocumentsSection";
import JourneySection from "./config-sections/JourneySection";
import ProductOptionsSection from "./config-sections/ProductOptionsSection";

const TAB_SECTIONS = [
  {
    id: "contract",
    icon: MdOutlineAssignmentTurnedIn,
    translationKey: "CONTRACT",
  },
  {
    id: "products",
    icon: MdOutlineInventory2,
    translationKey: "PRODUCTS_OPTIONS",
  },
  { id: "journey", icon: MdRoute, translationKey: "JOURNEY" },
  { id: "documents", icon: MdOutlineDescription, translationKey: "DOCUMENTS" },
];

export default function ContractConfig({ enterprise }) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const [activeSection, setActiveSection] = useState("contract");

  const [products, setProducts] = useState([]);
  const [hasNotice, setHasNotice] = useState(false);
  const [uploadingNotice, setUploadingNotice] = useState(false);
  const [noticeAttachment, setNoticeAttachment] = useState(null);
  const [selectedProductsData, setSelectedProductsData] = useState([]);

  /** Prevents duplicate CREATE_ATTACHMENT when Ant Upload fires onChange twice. */
  const noticeUploadInFlightRef = useRef(false);

  const [addAttachment] = useMutation(graphql.mutations.CREATE_ATTACHMENT);
  const [addDistribution] = useMutation(CREATE_DISTRIBUTION);
  const [deleteDistribution] = useMutation(DELETE_DISTRIBUTION);
  const { data: noticeData, refetch: refetchNoticeAttachment } = useQuery(
    graphql.queries.ATTACHMENTS,
    {
      variables: {
        where: {
          organization: { id: enterprise?.id },
        },
        isIn: {
          type: [INSURANCE_PLAN_ATTACHMENTS_TYPES.INFO_NOTICE],
        },
      },
      skip: !enterprise?.id,
      fetchPolicy: "network-only",
    },
  );
  const { data: insurancePlansData } = useQuery(INSURANCE_PLANS, {
    variables: {
      take: null,
      orderBy: [{ name: "ASC" }],
    },
  });
  const { data: distributionsData, refetch: refetchDistributions } = useQuery(
    DISTRIBUTIONS,
    {
      variables: {
        where: {
          broker: {
            organization: {
              id: enterprise?.id,
            },
          },
        },
      },
      skip: !enterprise?.id,
    },
  );

  const selectedProductIds = useMemo(
    () => selectedProductsData.map((product) => product.id),
    [selectedProductsData],
  );

  useEffect(() => {
    const attachment = get(noticeData, "attachments.data[0]", null);
    setNoticeAttachment(attachment);
  }, [noticeData]);

  useEffect(() => {
    setHasNotice(!!noticeAttachment);
  }, [noticeAttachment]);

  useEffect(() => {
    const allPlans = get(insurancePlansData, "insurancePlans.data", []) || [];
    setProducts(
      allPlans.map((plan) => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
      })),
    );
  }, [insurancePlansData]);

  useEffect(() => {
    const allDistributions = (
      get(distributionsData, "distributions.data", []) || []
    )
      .filter(
        (distribution) => distribution?.id && distribution?.insurancePlan?.id,
      )
      .map((distribution) => ({
        id: distribution.insurancePlan.id,
        name: distribution.insurancePlan.name,
        description: distribution.insurancePlan.description,
        distributionId: distribution.id,
      }));

    setSelectedProductsData(allDistributions);
  }, [distributionsData]);

  const productSelectOptions = useMemo(
    () =>
      products.map((product) => ({
        label: product.name,
        value: product.id,
        description: product.description,
      })),
    [products],
  );

  async function handleAddProduct(productId) {
    if (!productId || !enterprise?.broker?.id) return;

    const product = products.find((item) => item.id === productId);
    if (!product) return;

    try {
      await addDistribution({
        variables: {
          data: {
            id: cuid(),
            insurancePlan: { id: productId },
            broker: { id: enterprise.broker.id },
          },
        },
      });

      setSelectedProductsData((prev) => {
        if (prev.some((p) => p.id === productId)) {
          return prev;
        }
        return [...prev, product];
      });

      await refetchDistributions?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout du produit :", error);
    }
  }

  async function handleDeleteProduct(productId) {
    if (!productId) return;

    const product = selectedProductsData.find((p) => p.id === productId);
    if (!product) {
      console.error("Produit non trouvé :", productId);
      return;
    }

    // Get distributionId from product or find it from distributionsData
    let distributionId = product.distributionId;

    if (!distributionId) {
      // Try to find the distribution by insurancePlan id instead
      const distribution = get(
        distributionsData,
        "distributions.data",
        [],
      ).find((d) => d.insurancePlan?.id === productId);
      if (!distribution?.id) {
        console.error(
          "Impossible de trouver la distribution pour le produit :",
          productId,
        );
        alert(
          "Impossible de trouver la distribution associée. Veuillez rafraîchir la page.",
        );
        return;
      }
      distributionId = distribution.id;
    }

    try {
      await deleteDistribution({
        variables: {
          where: {
            id: distributionId,
          },
        },
      });

      setSelectedProductsData((prev) => prev.filter((p) => p.id !== productId));

      await refetchDistributions?.();
    } catch (error) {
      console.error("Erreur lors de la suppression du produit :", error);
      alert("Erreur lors de la suppression du produit. Veuillez réessayer.");
    }
  }

  async function handleNoticeUpload(file) {
    if (!file || !enterprise?.id) return;
    if (file.contentType !== CONTENT_TYPES.PDF) {
      alert("Veuillez sélectionner un fichier PDF");
      return;
    }

    if (noticeUploadInFlightRef.current) {
      return;
    }
    noticeUploadInFlightRef.current = true;

    setUploadingNotice(true);

    const payloadId = noticeAttachment?.id || cuid();

    try {
      const result = await addAttachment({
        variables: {
          data: {
            id: payloadId,
            name: file.name,
            content: file.base64,
            contentType: file.contentType,
            type: INSURANCE_PLAN_ATTACHMENTS_TYPES.INFO_NOTICE,
            organization: { id: enterprise.id },
          },
        },
      });

      const attachment = get(result, "data.addAttachment", null);

      if (attachment) {
        setNoticeAttachment(attachment);
        setHasNotice(true);
      }

      if (refetchNoticeAttachment) {
        await refetchNoticeAttachment();
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout de la notice :", error);
    } finally {
      noticeUploadInFlightRef.current = false;
      setUploadingNotice(false);
    }
  }

  const sections = useMemo(
    () =>
      TAB_SECTIONS.map((section) => ({
        ...section,
        label: t(`DETAIL.TABS.${section.translationKey}`),
      })),
    [t],
  );

  return (
    <div className="company__space-y-6">
      <div className="company__flex company__gap-2 company__border-b company__border-slate-200 company__overflow-x-auto">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`company__tab company__flex company__items-center company__gap-2 company__px-4 company__py-3 company__font-medium company__whitespace-nowrap ${
                activeSection === section.id
                  ? "company__tab--active"
                  : "company__text-slate-600"
              }`}
            >
              <Icon className="company__w-5 company__h-5" />
              {section.label}
            </button>
          );
        })}
      </div>

      <div style={{ minHeight: "600px" }}>
        {activeSection === "contract" && (
          <ContractSection organization={enterprise} />
        )}

        {activeSection === "products" && (
          <ProductOptionsSection
            availableOptions={productSelectOptions}
            selectedProducts={selectedProductsData}
            onAddProduct={handleAddProduct}
            onDeleteProduct={handleDeleteProduct}
          />
        )}

        {activeSection === "journey" && (
          <JourneySection
            contract={enterprise?.counterpartyContracts?.[0] || null}
          />
        )}

        {activeSection === "documents" && (
          <DocumentsSection
            hasNotice={hasNotice}
            uploadingNotice={uploadingNotice}
            onNoticeUpload={handleNoticeUpload}
            noticeAttachment={noticeAttachment}
            selectedProducts={selectedProductIds}
            products={products}
          />
        )}

        {activeSection === "communication" && <CommunicationSection />}
      </div>
    </div>
  );
}
